import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { getFullName } from "../../careprovider/visits/utils";
import { getLocalTimeZoneDateFormat } from "../../../utils/dateutils";

const BASE_HEADERS = [
  "Volunteer Coordinator",
  "Date of Request",
  "Services",
  "Patient Name",
  "MRN",
];

const DEFAULT_OPPORTUNITY_STATUS_COLUMNS = ["Assigned", "Shared", "Unassigned", "Rejected"];

function collectOpportunityStatusColumns(carePlans) {
  const extraStatuses = new Set();

  for (const plan of carePlans) {
    for (const opportunity of plan?.opportunities ?? []) {
      const status = String(opportunity?.opportunityStatus ?? "").trim();
      if (status && !DEFAULT_OPPORTUNITY_STATUS_COLUMNS.includes(status)) {
        extraStatuses.add(status);
      }
    }
  }

  return [
    ...DEFAULT_OPPORTUNITY_STATUS_COLUMNS,
    ...[...extraStatuses].sort((a, b) => a.localeCompare(b)),
  ];
}

function buildHeaders(statusColumns) {
  return [...BASE_HEADERS, ...statusColumns];
}

export const safeSheetName = (name) => {
  const base = String(name || "Unassigned")
    .replace(/[/:?*[\]\\]/g, "")
    .trim()
    .slice(0, 31);
  return base || "Team";
};

function formatRequestDate(requestDate) {
  if (requestDate == null || requestDate === "") return "";
  const fmt = getLocalTimeZoneDateFormat() || "MM/DD/YYYY";
  return moment(requestDate).format(fmt);
}

function formatServices(services) {
  if (services == null || services === "") return "";
  try {
    const parsed =
      typeof services === "string" ? JSON.parse(services) : services;
    if (!Array.isArray(parsed)) return "";
    if (parsed[0]?.formName) {
      return parsed.map((item) => item.formName).filter(Boolean).join(", ");
    }
    return parsed
      .map((item) => {
        if (typeof item === "string") {
          try {
            return JSON.parse(item).title;
          } catch {
            return "";
          }
        }
        return item?.title || "";
      })
      .filter(Boolean)
      .join(", ");
  } catch {
    return "";
  }
}

function shouldExportCarePlan(carePlan) {
  const patient = carePlan?.patient;
  if (!patient) return true;
  return patient.patientStatus === "Alive";
}

function teamLabelForPatient(patient) {
  if (!patient) return "Non-Patient Activity";
  if (typeof patient.teamName === "string" && patient.teamName.trim()) {
    return patient.teamName.trim();
  }
  const named = patient.teams?.map((t) => t?.name).find((n) => n?.trim?.());
  if (named?.trim()) return named.trim();
  return "Unassigned";
}

function formatVolunteerName(opportunity) {
  const name =
    getFullName(
      opportunity?.workforce?.givenName,
      opportunity?.workforce?.familyName,
    ) ?? "";
  return String(name).trim();
}

function formatVolunteersByStatus(opportunities, statusColumns) {
  return statusColumns.map((status) => {
    if (!Array.isArray(opportunities) || !opportunities.length) return "";

    return opportunities
      .filter((opportunity) => opportunity?.opportunityStatus === status)
      .map(formatVolunteerName)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .join(", ");
  });
}

/** Normalizes null, undefined, empty string, and whitespace-only to "". */
function emptyString(value) {
  if (value == null) return "";
  const s = typeof value === "string" ? value.trim() : String(value).trim();
  return s === "" ? "" : s;
}

function buildRow(carePlan, statusColumns) {
  const volunteerColumns = formatVolunteersByStatus(
    carePlan?.opportunities,
    statusColumns,
  );

  return [
    emptyString(
      getFullName(
        carePlan?.coordinator?.givenName,
        carePlan?.coordinator?.familyName,
      ),
    ),
    emptyString(formatRequestDate(carePlan?.requestDate)),
    emptyString(formatServices(carePlan?.services)),
    emptyString(
      getFullName(
        carePlan?.patient?.givenName,
        carePlan?.patient?.familyName,
      ),
    ),
    emptyString(carePlan?.patient?.medicalRecordNumber),
    ...volunteerColumns.map((value) => emptyString(value)),
  ];
}

export async function exportCarePlansToExcel(carePlans) {
  if (!Array.isArray(carePlans) || !carePlans.length) {
    return false;
  }

  const exportablePlans = carePlans.filter(shouldExportCarePlan);
  if (!exportablePlans.length) {
    return false;
  }

  const statusColumns = collectOpportunityStatusColumns(exportablePlans);
  const headers = buildHeaders(statusColumns);

  const byTeam = {};
  for (const plan of exportablePlans) {
    const key = teamLabelForPatient(plan?.patient);
    if (!byTeam[key]) byTeam[key] = [];
    byTeam[key].push(plan);
  }

  const workbook = new ExcelJS.Workbook();
  const usedSheetNames = new Set();

  for (const [teamName, plans] of Object.entries(byTeam)) {
    let baseName = safeSheetName(teamName);
    let sheetName = baseName;
    let n = 1;
    while (usedSheetNames.has(sheetName)) {
      sheetName = `${baseName.slice(0, 22)}_${n++}`;
    }
    usedSheetNames.add(sheetName);

    const sheet = workbook.addWorksheet(sheetName);
    sheet.views = [{ state: "frozen", ySplit: 1 }];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    for (const plan of plans) {
      const row = sheet.addRow(buildRow(plan, statusColumns));
      row.eachCell((cell) => {
        cell.alignment = {
          horizontal: "left",
          vertical: "middle",
          wrapText: true,
        };
      });
    }

    sheet.columns.forEach((col) => {
      let maxLength = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const value = cell.value != null ? String(cell.value) : "";
        maxLength = Math.max(maxLength, value.length);
      });
      col.width = Math.min(maxLength + 2, 60);
    });
  }

  const timestamp = new Date()
    .toISOString()
    .replace(/[:.]/g, "-")
    .replace("T", "_")
    .slice(0, 19);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Utilization_${timestamp}.xlsx`);
  return true;
}
